/** Email & Message Centre → Who receives what: pick a team member, see every automated text / email / push grouped, tick what they get. */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, Loader2, Mail, Phone, Users } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { formatApiError } from '@/utils/apiError';
import { useAuth } from '@/contexts/AuthContext';
import { PersonPicker } from '@/components/messages/PersonPicker';
import { PushGroups, ScheduledGroups } from '@/components/messages/PersonMessageGroups';
import { AddMobileInline } from '@/components/messages/RecipientPicker';

async function api(method, path, body) {
  const r = await fetch(`${API_URL}/api/staff/message-centre/${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(d) || 'Request failed');
  return d;
}

function PersonHeader({ p, onAddMobile }) {
  return (
    <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-4" data-testid="person-header">
      <div className="w-12 h-12 rounded-full bg-gray-900 text-white font-bold flex items-center justify-center text-base">{(p.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-lg font-bold text-gray-900" data-testid="person-name">{p.name}</div>
        <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1 mt-0.5">
          <span className="capitalize">{p.role}{p.profit_access ? ' · profit access' : ''}</span>
          <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone || <span className="text-amber-700 font-semibold inline-flex items-center gap-1">No mobile saved {onAddMobile && <AddMobileInline person={p} onSave={onAddMobile} testId="person-add-mobile" />}</span>}</span>
          <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {p.email || 'No email'}</span>
        </div>
      </div>
      <p className="text-[11px] text-gray-500 max-w-xs">Switch a message on to add {p.name?.split(' ')[0]} to that list, off to take them off it. Daily, weekly and monthly profit share one list. Changes save instantly.</p>
    </div>
  );
}

export default function MessagePeople() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const selected = params.get('user') || '';
  const [people, setPeople] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api('GET', 'people').then((d) => { setPeople(d.people); if (!selected && d.people[0]) setParams({ user: d.people[0].user_id }, { replace: true }); }).catch((e) => toast.error(e.message)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try { setData(await api('GET', `people/${selected}/messages`)); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [selected]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, channel, on) => {
    const flip = (d) => ({ ...d, scheduled: d.scheduled.map((g) => ({ ...g, jobs: g.jobs.map((j) => (j.key === key && channel !== 'push' ? { ...j, [channel]: { ...j[channel], on } } : j)) })),
      push: d.push.map((g) => ({ ...g, events: g.events.map((e) => (e.key === key && channel === 'push' ? { ...e, on } : e)) })) });
    setData(flip);
    try { setData(await api('PUT', `people/${selected}/messages/${key}`, { channel, on })); toast.success(on ? 'Added' : 'Removed'); } catch (e) { toast.error(e.message); load(); }
  };
  const addMobile = async (userId, phone) => {
    try { const d = await api('PUT', `schedules/people/${userId}/mobile`, { phone }); setPeople(d.people); toast.success(`Mobile saved — ${d.phone}`); load(); } catch (e) { toast.error(e.message); throw e; }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="message-people-page">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #3b1f5e 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button type="button" onClick={() => navigate('/staff/message-centre')} className="p-2 hover:bg-white/10 rounded-lg" aria-label="Back" data-testid="people-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5" style={{ color: '#D4A24B' }} /> Who receives what</h1>
            <p className="text-white/70 text-xs">Pick a team member and tick the texts, emails and push notifications they should get.</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/staff/message-centre/schedules')} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="people-to-schedules"><CalendarClock className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Times &amp; schedules</span></Button>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-5 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {!people ? <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div> : <PersonPicker people={people} selected={selected} onSelect={(id) => setParams({ user: id })} />}
        <div className="space-y-4 min-w-0">
          {loading && !data && <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}
          {data && (<>
            <PersonHeader p={data.person} onAddMobile={isAdmin ? addMobile : null} />
            <h2 className="text-base md:text-lg font-bold text-gray-900 pt-2">Scheduled texts &amp; emails</h2>
            <ScheduledGroups groups={data.scheduled} onToggle={toggle} canEdit={isAdmin} />
            <h2 className="text-base md:text-lg font-bold text-gray-900 pt-2">Push notifications</h2>
            <PushGroups groups={data.push} onToggle={toggle} canEdit={isAdmin} />
            {!isAdmin && <p className="text-[11px] text-gray-400">Only admins can change these.</p>}
          </>)}
        </div>
      </div>
    </div>
  );
}
